import { layoutWorkspace } from '../../_common/layout.workspace';
import { buildingParameterEntity } from './building-parameters.entity';
import { createRouteFor } from '@components-common/routes';
import { dataSection, dataSectionBase } from '../routes.section';
import { clone } from 'ramda';
import { PageFormToolbar } from '../../../components-common/layout/views/PageFormToolbar';
import { SpaceFunctionForm } from '../../../components-domain/bim-domain/spaces/SpaceFunctionForm';
import { IndexContainer } from '../../../components-common/containers/IndexContainer';
import {
  editParameterScreen,
  newParameterScreen,
  parametersTab,
  toolbar_components,
} from '../components/_common_props';
import { TabbedPage } from '../../../components-common/tabs/TabbedPage';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

const basePath = dataSectionBase;

/**
 * Array: spaceFunctionsTabs
 *
 * This array contains the tab configuration for the Space Functions section.
 * Each tab includes an ID, title, and components to be rendered within the tab.
 *
 * @property {string} id - The ID of the tab.
 * @property {string} title - The title of the tab.
 * @property {Array} components - An array of components to be rendered within the tab.
 */
const spaceFunctionsTabs = [
  {
    id: 'space_functions',
    title: 'Space Functions',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            ...toolbar_components,
            left: [
              {
                id: 'create',
                kind: 'create',
                onAction: (props: any) => props.navigate('spaces/new'),
              } as any,
            ],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'table_components',
            props: {
              source: {
                type: 'REMOTE',
                url: '/buildingms/space-functions',
                key: '/buildingms/space-functions',
              },
              columns: ['name', 'description', { id: 'sourceType' }],
              actions: [
                {
                  id: 'edit',
                  onClick: (props: any) =>
                    props.navigate(`spaces/${props.row.id}`),
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Edit space function',
                },
                {
                  id: 'copy',
                  actionToDispatch: {
                    payload: {
                      copy: '/buildingms/space-functions/clone',
                    },
                  },
                  displayTooltip: true,
                  tooltipText: 'Duplicate space function',
                } as any,
                {
                  id: 'delete',
                  actionToDispatch: {
                    payload: {
                      endpointToDelete: '/buildingms/space-functions',
                    },
                  },
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Delete space function',
                },
              ],
            },
          },
        },
      },
    ],
  },
  parametersTab('SPACES'),
];

/**
 * Configuration Object: formScreen
 *
 * This object defines the configuration for the form screen of space functions.
 * It specifies the component, form, template, validation schema, and initial empty values.
 *
 * @property {React.Component} Component - The component to render the form toolbar.
 * @property {React.Component} form - The form component used for managing space function forms.
 * @property {string} template - The template URL for the space functions.
 * @property {Object} schema - The validation schema for the form.
 * @property {Object} empty - The initial empty values for the form fields.
 */
const formScreen = {
  Component: PageFormToolbar,
  form: SpaceFunctionForm,
  template: '/buildingms/space-functions',
  schema: buildingParameterEntity.schema,
  empty: {
    name: '',
    description: '',
    typeParameters: [],
  },
};

/**
 * Configuration Object: spaceFunctionsSection
 *
 * This object defines the configuration for the space functions section.
 * It includes the section ID, title, path, layout, component, and an array of screen configurations.
 *
 * @property {string} id - The ID of the section.
 * @property {string} title - The title of the section.
 * @property {string} path - The URL path for the section.
 * @property {React.Component} layout - The layout component for the section.
 * @property {React.Component} Component - The component to render the tabbed page.
 * @property {Array} screens - An array of screen configuration objects.
 */
export const spaceFunctionsSection: any = {
  id: 'space_functions',
  title: 'Space Functions',
  path: 'space-functions',
  tmp: 'tabbed',
  layout: layoutWorkspace,
  Component: TabbedPage,
  screens: [
    {
      id: 'spaceFunctions_tabs',
      path: '',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tabs: spaceFunctionsTabs,
    },
    {
      ...clone(formScreen),
      left: ['create'],
      title: 'Create Space Function',
      id: 'spaces_create',
      path: 'spaces/new',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'create',
    },
    {
      ...clone(formScreen),
      left: ['save'],
      title: 'Edit Space Function',
      right: ['back'],
      id: 'spaces_edit',
      path: 'spaces/:spaceFunctionId',
      editSource: `${formScreen.template}/`,
      idPropSource: 'spaceFunctionId',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'edit',
    },
    newParameterScreen('SPACES'),
    editParameterScreen('SPACES'),
  ],
};

/**
 * Function: createRouteFor
 *
 * This function generates routes for the space functions section.
 *
 * @param {ConfigSection} dataSection - The data section configuration.
 * @param {string} routeKey - The key for the route.
 *
 * @returns {Object} An object containing the generated routes for space functions.
 */
const routesForSpaceFunctions = createRouteFor<
  typeof dataSection,
  'space_functions'
>(dataSection, 'space_functions');

/**
 * Object: spaceFunctionsRoutes
 *
 * This object defines the routes for the space functions section.
 *
 * @property {Object} index - The route configuration for the index page.
 * @property {Object} create - The route configuration for the create page.
 * @property {Object} details - The route configuration for the details page.
 */
export const spaceFunctionsRoutes = {
  index: routesForSpaceFunctions('index', { basePath }),
  create: routesForSpaceFunctions('create', { basePath }),
  details: routesForSpaceFunctions('details', { basePath }),
};

/**
 * Type: SpaceFunctionsRoutesIds
 *
 * This type defines the IDs for the space functions routes.
 *
 * @typedef {string} SpaceFunctionsRoutesIds - The ID of the space functions route.
 */
export type SpaceFunctionsRoutesIds =
  typeof spaceFunctionsRoutes[keyof typeof spaceFunctionsRoutes]['id'];
